<template>
    <VueDraggableResizable
        drag-handle=".kleptos-window-titlebar"
        drag-cancel=".kleptos-window-titlebar-btns"
        :active="gameObject.windowData.active"
        :class="{ 'is-maximized': gameObject.windowData.maximized }"
        :draggable="gameObject.windowData.draggable"
        :enable-native-drag="true"
        :lock-aspect-ratio="gameObject.windowData.lockAspectRatio"
        :min-width="gameObject.windowData.minWidth"
        :min-height="gameObject.windowData.minHeight"
        :max-width="gameObject.windowData.maxWidth"
        :max-height="gameObject.windowData.maxHeight"
        :minimized="gameObject.windowData.minimized"
        :parent="true"
        :resizable="gameObject.windowData.resizable"
        :w="gameObject.windowData.width"
        :h="gameObject.windowData.height"
        :x="x"
        :y="y"
        :z="gameObject.windowData.z"
        @dragging="onDragging"
        @dragstop="onDragStop"
        @resizing="onResizing"
        @resizestop="onResizeStop"
        @deactivated="onDeactivated"
        @mousedown.native="onActivated"
        @dblclick.native="onMaximize"
    >
        <slot/>

    </VueDraggableResizable>
</template>

<script>
    import { mapActions } from 'vuex';
    import VueDraggableResizable from 'vue-draggable-resizable';

    export default {
        components: {
            VueDraggableResizable
        },
        props: {
            gameObject: {
                type: Object,
                required: true
            },
            resWidth: {
                type: Number,
                required: true
            },
            resHeight: {
                type: Number,
                required: true
            }
        },
        data() {
            return {
                x: null,
                y: null
            }
        },
        created() {
            this.x = this.getX(this.gameObject.windowData.x, this.gameObject.windowData.width);
            this.y = this.getY(this.gameObject.windowData.y, this.gameObject.windowData.height);
        },
        //--------------------------------------------------------------------
        // MOUNTED
        // update x, y so windowManager 'actions.js' can offset based on existing windows
        //--------------------------------------------------------------------

        mounted() {
            this.correctPositionsDimensionsAndSave(this.x, this.y); // [LOCKED] - need this so open windows can offset x,y. See Actions.js
        },
        //--------------------------------------------------------------------
        // METHODS
        //--------------------------------------------------------------------

        methods: {
            //--------------------------------------------------------------------
            // GET X
            // if no x supplied, center it on horizontally
            //--------------------------------------------------------------------

            getX(x, width) {
                if (x === null) {
                    const offsetX = this.resWidth * 2; // [LEFT OFFSET] [>>CLIENT<<] [RIGHT OFFSET]
                    const stageCenterX = (document.documentElement.clientWidth + offsetX) / 2 - width / 2;
                    return Math.floor(stageCenterX);
                }
                return Math.floor(x);
            },
            //--------------------------------------------------------------------
            // GET Y
            // if no y supplied, center it on vertically
            //--------------------------------------------------------------------

            getY(y, height) {
                if (y === null) {
                    const stageCenterY = ((document.documentElement.clientHeight)/2 - height / 2) - 36*2; // 36 is topbar, better center
                    return Math.floor(stageCenterY);
                }
                return Math.floor(y);
            },
            //--------------------------------------------------------------------
            // ON MAXIMIZE
            // The window mousedown event overrides the dblclick inside the child.
            // We sadly need to place it here to work.
            //--------------------------------------------------------------------

            onMaximize() {
                if (this.gameObject.windowData.maximizable) {
                    if (event.target.className === 'kleptos-window-titlebar') {
                        this.$store.dispatch('gameOS/maximizeWindow', this.gameObject);
                    }
                }
            },
            //--------------------------------------------------------------------
            // ON RESIZING
            //--------------------------------------------------------------------

            onResizing(x, y, width, height) {
                // ...
            },
            //--------------------------------------------------------------------
            // ON RESIZE STOP
            //--------------------------------------------------------------------

            onResizeStop(x, y, width, height) {
                this.correctPositionsDimensionsAndSave(x, y, width, height);
            },
            //--------------------------------------------------------------------
            // ON DRAGGING
            //--------------------------------------------------------------------

            onDragging(x, y) {
                this.x = x;
                this.y = y;
            },
            //--------------------------------------------------------------------
            // ON DRAG STOP
            //--------------------------------------------------------------------

            onDragStop(x, y) {
                this.correctPositionsDimensionsAndSave(x, y);
            },
            //--------------------------------------------------------------------
            // CORRECT POSITIONS/DIMENSIONS AND SAVE
            //--------------------------------------------------------------------

            correctPositionsDimensionsAndSave(x, y, width, height) {
                const boundaryBottom = document.documentElement.clientHeight - 110;
                const boundaryLeft = this.resWidth - this.$el.offsetWidth + 40;
                const boundaryRight = this.resWidth + document.documentElement.clientWidth - 40;
                const boundariesExceeded = !!(x < boundaryLeft || x > boundaryRight || y > boundaryBottom);

                let safeX = x,
                    safeY = y;

                // if too far left, whip the window back
                if (x < boundaryLeft) safeX = boundaryLeft;

                // if too far right, whip the window back
                if (x > boundaryRight) safeX = boundaryRight;

                // if too far down, whip the window back
                if (y > boundaryBottom) safeY = boundaryBottom;

                // prepare final object for dispatch
                let finalPosObj = {
                    target: this.gameObject,
                    x: Math.floor(safeX),
                    y: Math.floor(safeY)
                }

                // if resizing, add them to final object for dispatch
                if (width) finalPosObj.width = width;
                if (height) finalPosObj.height = height;

                // save to store
                this.$store.dispatch('gameOS/updateWindow', finalPosObj)
                    .then(() => {
                        if (boundariesExceeded) {
                            this.x = this.gameObject.windowData.x;
                            this.y = this.gameObject.windowData.y;
                        }
                    }
                );

                console.log(finalPosObj);
            },
            //--------------------------------------------------------------------
            // ON DEACTIVATED
            // * Note: windows will deactivate when clicking other windows OR outside (desktop)
            // * this ensures user can reselect once deactivated (test with desktop).
            //--------------------------------------------------------------------

            onDeactivated(){
                this.$store.dispatch('gameOS/deselectWindows', this.gameObject);
            },
            //--------------------------------------------------------------------
            // ON ACTIVATED
            // 1. triggers AFTER @deactivated
            // 2. replaced @activated="onActivated" on the instance, switched out for
            //    @onmousedown so we can activate the window on click anywhere, but still drag via the drag handle.
            // 3. perform check so not to select the window when closing or minimizing
            // 4. for maximizing, do active 'select' on mouse up (SPECIAL CASE)
            //--------------------------------------------------------------------

            onActivated() {
                const clickedThroughTitlebarBtn = event.target.classList.contains('kleptos-window-titlebar-btn');
                if (!clickedThroughTitlebarBtn) {
                    this.$store.dispatch('gameOS/selectWindow', this.gameObject);
                }
            }
        }
    }
</script>

<style lang="scss" scoped>

    @import '~vue-draggable-resizable/dist/VueDraggableResizable.css';

    //--------------------------------------------------------------------
    // IS-MAXIMIZED
    //--------------------------------------------------------------------

    .vdr.is-maximized {
        position: fixed !important;
        top: rem-calc(40) !important; // TODO: variable?
        left: 0 !important;
        width: 100% !important;
        height: calc(100% - #{rem-calc(80)}) !important; // TODO: variable?
        animation: subtle-enter 200ms ease 0s alternate 1 forwards;

        @keyframes subtle-enter {
            from { transform: scale(0.96); }
            to   { transform: scale(1); }
        }
    }

    //--------------------------------------------------------------------
    // VUE DRAGGABLE RESIZABLE OVERRIDES
    // https://vue-loader.vuejs.org/guide/scoped-css.html#deep-selectors
    //--------------------------------------------------------------------

    $drag-width: 8px; // 8px

    .vdr {
        border: 0;
        // border: 2px dashed yellow;
    }

    .vdr /deep/ .handle {
        margin: 0 !important;
        border: 0 !important;
        background: rgba(aqua, 0.3);
        opacity: 0; // comment for DEV
        z-index: 1; // yes or no?
    }

    .vdr /deep/ .handle.handle-tl,
    .vdr /deep/ .handle.handle-tr,
    .vdr /deep/ .handle.handle-br,
    .vdr /deep/ .handle.handle-bl {
        z-index: 1;
    }

    // TOP LEFT CORNER
    .vdr /deep/ .handle.handle-tl {
        top: -$drag-width/2;
        left: -$drag-width/2;
        cursor: nwse-resize;
    }

    // TOP
    .vdr /deep/ .handle.handle-tm {
        top: -$drag-width/2;
        left: 0;
        width: 100%;
        height: $drag-width;
        cursor: ns-resize;
    }

    // TOP RIGHT CORNER
    .vdr /deep/ .handle.handle-tr {
        top: -$drag-width/2;
        right: -$drag-width/2;
        cursor: nesw-resize;
    }

    // RIGHT
    .vdr /deep/ .handle.handle-mr {
        top: 0;
        right: -$drag-width/2;
        width: $drag-width;
        height: 100%;
        cursor: ew-resize;
    }

    // BOTTOM RIGHT CORNER
    .vdr /deep/ .handle.handle-br {
        bottom: -$drag-width/2;
        right: -$drag-width/2;
        width: 17px; // *more luxurious select space
        height: 17px; // *
        cursor: nwse-resize;
        z-index: 2;
    }

    // BOTTOM
    .vdr /deep/ .handle.handle-bm {
        bottom: -$drag-width/2;
        left: 0;
        width: 100%;
        height: $drag-width;
        cursor: ns-resize;
    }

    // BOTTOM LEFT CORNER
    .vdr /deep/ .handle.handle-bl {
        bottom: -$drag-width/2;
        left: -$drag-width/2;
        cursor: nesw-resize;
    }

    // LEFT
    .vdr /deep/ .handle.handle-ml {
        top: 0;
        left: -$drag-width/2;
        width: $drag-width;
        height: 100%;
        cursor: ew-resize;
    }
</style>